import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api';
import { customerEndpoints } from '../api';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars } from '../pages/event/shared';

/**
 * A customer's account credit (D-372): the balance, every entry behind it, and
 * the Adjust form.
 *
 * The balance is the sum of the entries and is never stored, so the table is
 * the statement and the total is its last line. An adjustment needs
 * `customers.credit.adjust`, a reason (kept on the entry and in the audit log)
 * and the policy's `staff_adjustments`; it can take a balance to zero but never
 * below, which the database refuses on its own.
 */
export function CustomerCreditPanel({ customerKey }: { customerKey: string }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const credit = useQuery({ queryKey: ['customers', 'credit', customerKey], queryFn: () => customerEndpoints.credit(customerKey) });
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<'ADD' | 'REMOVE'>('ADD');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const adjust = useMutation({
    mutationFn: () => {
      const cents = Math.round(Number(amount.replace(/[$,\s]/g, '')) * 100);
      return customerEndpoints.adjustCredit(customerKey, { amount_cents: direction === 'ADD' ? cents : -cents, reason: reason.trim(), idempotency_key: crypto.randomUUID() });
    },
    onSuccess: () => {
      setAmount('');
      setReason('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['customers', 'credit', customerKey] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const data = credit.data;
  if (credit.error) return <ErrorNote>{(credit.error as ApiError).message}</ErrorNote>;
  if (!data) return null;
  const account = data.credit;
  const amountOk = Number.isFinite(Number(amount.replace(/[$,\s]/g, ''))) && Number(amount.replace(/[$,\s]/g, '')) > 0;
  const mayAdjust = Boolean(account) && can('customers.credit.adjust') && data.policy.staff_adjustments;
  return (
    <Panel
      title="Account credit"
      description={
        account
          ? `${dollars(account.balance_cents)} on the account${account.verified ? '' : ' · not yet spendable: spends once they sign in with their email'}. ${data.policy.expires_after_days ? `Unspent credit expires after ${data.policy.expires_after_days} days.` : 'Credit does not expire.'}`
          : 'No account to hold credit: a guest who has never had credit.'
      }
    >
      {error ? (
        <div className="px-5 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      <Table headers={['When', 'What', 'Order', 'Amount', 'Balance', 'Reason']}>
        {data.entries.length === 0 ? (
          <EmptyRow columns={6}>No credit yet.</EmptyRow>
        ) : (
          data.entries.map((entry) => (
            <tr key={entry.id} className="border-b border-line last:border-0">
              <td className="px-5 py-2 whitespace-nowrap text-ink-600">{formatWhen(entry.created_at)}</td>
              <td className="px-5 py-2">
                <Badge tone={entry.amount_cents > 0 ? 'ok' : 'neutral'}>{entry.kind_name}</Badge>
              </td>
              <td className="px-5 py-2">{entry.order_id && entry.order_number ? <Link to={`/orders/${entry.order_id}`} className="text-accent hover:underline">{entry.order_number}</Link> : '—'}</td>
              <td className="px-5 py-2 tabular-nums">{entry.amount_cents > 0 ? '+' : '−'}{dollars(Math.abs(entry.amount_cents))}</td>
              <td className="px-5 py-2 tabular-nums">{dollars(entry.balance_after_cents)}</td>
              <td className="px-5 py-2 text-ink-600">{entry.reason ?? ''}</td>
            </tr>
          ))
        )}
      </Table>
      {mayAdjust ? (
        <form
          className="flex flex-wrap items-end gap-2 border-t border-line px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (amountOk && reason.trim()) adjust.mutate();
          }}
        >
          <label className="text-sm">
            <span className="block text-xs text-ink-600">Adjust</span>
            <select className={INPUT} value={direction} onChange={(e) => setDirection(e.target.value as 'ADD' | 'REMOVE')}>
              <option value="ADD">Add credit</option>
              <option value="REMOVE">Remove credit</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs text-ink-600">Amount ($)</span>
            <input className={INPUT} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="25.00" />
          </label>
          <label className="min-w-64 flex-1 text-sm">
            <span className="block text-xs text-ink-600">Reason (required, kept on the record)</span>
            <input className={INPUT} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
          </label>
          <Button type="submit" variant="primary" disabled={!amountOk || !reason.trim() || adjust.isPending}>
            {adjust.isPending ? 'Saving…' : 'Adjust'}
          </Button>
        </form>
      ) : null}
    </Panel>
  );
}
